// Roles (D51), after Isomorphic's roles-permissions page: a card per role (its members and how
// many permissions it grants), then the selected role's permission matrix grouped by range,
// creating and deleting roles and giving a user a role. The Super Admin always has every
// permission and cannot change. Each control shows only with its permission; the API checks.
import { Plus, ShieldCheck, ShieldStar, Trash } from "@phosphor-icons/react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { type FormEvent, useState } from "react";
import { Button, Input } from "rizzui";

import { accessQuery, can, type Me } from "../auth/access";
import { createRole, deleteRole, type Role, rolesQuery, usersQuery } from "../auth/admin";
import { useT } from "../i18n";
import { sectionCrumbs, SECTIONS } from "../layout/nav";
import { toast } from "../layout/toast";
import { cardClass, IconTile, WidgetCard } from "../ui/Card";
import { cn } from "../ui/cn";
import { PageHeader } from "../ui/PageHeader";
import { EmptyState, ErrorState, Loading } from "../ui/states";
import { Pill } from "../ui/status";
import { Assign, Matrix } from "./RolesMatrix";

const message = (e: unknown) => (e instanceof Error ? e.message : String(e));

function RoleCard({ role, members, selected, onSelect }: { role: Role; members?: number; selected: boolean; onSelect: () => void }) {
  const t = useT().users.roles;
  return (
    <button
      type="button"
      aria-current={selected ? "true" : undefined}
      onClick={onSelect}
      className={cn(cardClass, "flex w-full items-start gap-3 p-4 text-start transition-colors", selected ? "border-primary ring-2 ring-primary/20" : "hover:border-gray-300")}
    >
      <IconTile className={role.is_super_admin ? "bg-primary-lighter text-primary-dark" : "bg-secondary-lighter text-secondary-dark"}>
        {role.is_super_admin ? <ShieldStar weight="duotone" /> : <ShieldCheck weight="duotone" />}
      </IconTile>
      <span className="min-w-0 flex-1">
        <span className="flex flex-wrap items-center gap-1.5">
          <span className="truncate font-semibold text-gray-900">{role.name}</span>
          {role.is_system ? <Pill tone="gray" className="px-1.5 py-0 text-[10.5px]">{t.system}</Pill> : null}
        </span>
        <span className="mt-1 block text-xs text-gray-500">
          {role.is_super_admin ? t.everyPermission : t.permissions(role.permissions.length)}
          {members !== undefined ? ` · ${t.members(members)}` : ""}
        </span>
      </span>
    </button>
  );
}

function ManageRoles({ me }: { me: Me }) {
  const t = useT().users.roles;
  const crumbs = sectionCrumbs("roles");
  const roles = useQuery(rolesQuery());
  const users = useQuery({ ...usersQuery(), enabled: can(me, "ViewUsers") });
  const queryClient = useQueryClient();
  const [selected, setSelected] = useState<string>();
  const [name, setName] = useState("");
  const refresh = () => queryClient.invalidateQueries({ queryKey: ["roles"] });
  const role = roles.data?.find((r) => r.id === selected) ?? roles.data?.[0];
  const members = (r: Role) => users.data?.filter((u) => u.role_id === r.id).length;

  async function create(e: FormEvent) {
    e.preventDefault();
    try {
      const created = await createRole(name.trim(), "");
      setName("");
      await refresh();
      setSelected(created.id);
    } catch (err) {
      toast(message(err), "warn");
    }
  }

  async function remove(r: Role) {
    try {
      await deleteRole(r.id);
      toast(t.deleted(r.name));
      setSelected(undefined);
      await refresh();
    } catch (err) {
      toast(message(err), "warn");
    }
  }

  return (
    <>
      <PageHeader title={SECTIONS.roles.title} breadcrumb={crumbs} description={t.description} />
      {roles.isPending ? <Loading /> : null}
      {roles.isError ? <ErrorState message={roles.error.message} /> : null}
      {roles.data && role ? (
        <div className="space-y-5">
          <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4" aria-label={t.listLabel} role="list">
            {roles.data.map((r) => (
              <div role="listitem" key={r.id}>
                <RoleCard role={r} members={members(r)} selected={r.id === role.id} onSelect={() => setSelected(r.id)} />
              </div>
            ))}
            {can(me, "CreateRole") ? (
              <form className={cn(cardClass, "flex items-center gap-2 border-dashed p-4")} aria-label={t.createForm} onSubmit={(e) => void create(e)}>
                <Input className="min-w-0 flex-1" size="sm" placeholder={t.newRole} aria-label={t.newRoleName} value={name} onChange={(e) => setName(e.target.value)} required />
                <Button size="sm" type="submit" aria-label={t.create}>
                  <Plus weight="bold" className="h-4 w-4" />
                </Button>
              </form>
            ) : null}
          </div>
          <WidgetCard
            title={role.name}
            description={role.is_super_admin ? t.holdsEvery : t.grouped}
            action={
              can(me, "RemoveRole") && !role.is_system ? (
                <Button size="sm" variant="outline" color="danger" className="gap-1.5" onClick={() => void remove(role)}>
                  <Trash className="h-4 w-4" /> {t.remove}
                </Button>
              ) : null
            }
          >
            <Matrix key={role.id + role.updated_at} role={role} me={me} onSaved={refresh} />
          </WidgetCard>
          {can(me, "AssignUserRole") && can(me, "ViewUsers") ? (
            <WidgetCard title={t.assignTitle} description={t.assignDescription}>
              <Assign roles={roles.data} />
            </WidgetCard>
          ) : null}
        </div>
      ) : null}
    </>
  );
}

export default function RolesPage() {
  const t = useT().users.roles;
  const crumbs = sectionCrumbs("roles");
  const me = useQuery(accessQuery());
  if (me.isPending) return <Loading />;
  if (!me.data || !can(me.data, "ViewRoles")) {
    return (
      <>
        <PageHeader title={SECTIONS.roles.title} breadcrumb={crumbs} />
        <EmptyState icon={<ShieldCheck weight="duotone" />} title={t.cannotSee} />
      </>
    );
  }
  return <ManageRoles me={me.data} />;
}
